/**
 * Utility functions for the PublicationsPage components.
 * Replicates patterns from ArchiveList and ArchiveFileLink.
 */

import * as moment from 'moment';
import 'moment/locale/de';
import { escapeStringRegexp } from '@utils/helpers';

// Re-export for convenience so consumers only need to import from utils
export { escapeStringRegexp };

// ---------------------------------------------------------------------------
// Umlaut handling (replicated from ArchiveList)
// ---------------------------------------------------------------------------

const umlautMap: { [key: string]: string } = {
  Ü: 'UE',
  Ä: 'AE',
  Ö: 'OE',
  ü: 'ue',
  ä: 'ae',
  ö: 'oe',
  ß: 'ss',
};

/**
 * Normalise German umlauts for case-insensitive comparison.
 * Matches the `replaceUmlaute` implementation in ArchiveList.
 */
export const replaceUmlaute = (str: string): string => {
  if (!str || typeof str !== 'string') {
    return '';
  }

  return str
    .replace(/[\u00dc|\u00c4|\u00d6][a-z]/g, (a) => {
      const big = umlautMap[a.slice(0, 1)];
      return big.charAt(0) + big.charAt(1).toLowerCase() + a.slice(1);
    })
    .replace(new RegExp(`[${Object.keys(umlautMap).join('|')}]`, 'g'), (a) => umlautMap[a]);
};

// ---------------------------------------------------------------------------
// File type icon mapping
// ---------------------------------------------------------------------------

/**
 * Derive a file-type icon URL from a MIME type.
 *
 * The ArchiveFileLink component receives its icon URL from the GraphQL
 * `icon` field (server-provided). PublicationDocument does not have an
 * `icon` field, so we map from `mimeType` to the static asset paths
 * available in the project.
 */
export const getFileTypeIcon = (mimeType: string | null | undefined): string => {
  if (!mimeType) {
    return '/static/front/assets/document-icons/xlsx.png'; // generic fallback
  }

  const mime = mimeType.toLowerCase();

  if (mime === 'application/pdf') {
    return '/static/front/assets/document-icons/pdf.png';
  }

  if (mime === 'application/vnd.ms-excel' || mime.includes('spreadsheetml') || mime.includes('excel')) {
    return '/static/front/assets/document-icons/xlsx.png';
  }

  if (mime === 'application/msword' || mime.includes('wordprocessingml') || mime.includes('word')) {
    return '/static/front/assets/document-icons/doc.png';
  }

  // Fallback for unknown types
  return '/static/front/assets/document-icons/xlsx.png';
};

// ---------------------------------------------------------------------------
// Format label (matching ArchiveFileLink's cleanFormat logic)
// ---------------------------------------------------------------------------

/**
 * Derive a clean format label from a MIME type string.
 *
 * ArchiveFileLink uses: `format.replace(/^[\w\-]+\/([\w\-]+)$/, '$1')`
 * which extracts the subtype portion of the MIME type (e.g. "pdf" from
 * "application/pdf"). We replicate the same regex here.
 */
export const getFormatLabel = (mimeType: string | null | undefined): string => {
  if (!mimeType) {
    return '—';
  }

  const cleaned = mimeType.replace(/^[\w\-]+\/([\w\-]+)$/, '$1');

  // Shorten common verbose subtypes
  if (cleaned.includes('spreadsheetml') || cleaned.includes('ms-excel')) {
    return 'xls';
  }
  if (cleaned.includes('wordprocessingml') || cleaned === 'msword') {
    return 'doc';
  }

  return cleaned;
};

// ---------------------------------------------------------------------------
// File size formatting
// ---------------------------------------------------------------------------

/**
 * Format a byte count as a human-readable KB string.
 */
export const formatFileSize = (bytes: number | null | undefined): string => {
  if (!bytes) {
    return '';
  }
  return `${Math.round(bytes / 1024)} KB`;
};

// ---------------------------------------------------------------------------
// Date formatting
// ---------------------------------------------------------------------------

/** Date formats accepted by the backend, matching ArchiveList. */
const dateFormats = ['YYYY-MM-DD', 'DD.MM.YYYY', 'MM/DD/YYYY'];

/**
 * Format a `fileDate` string for display, respecting the current language.
 *
 * - German → DD.MM.YYYY (e.g. "15.01.2024")
 * - English → MM/DD/YYYY (e.g. "01/15/2024")
 */
export const formatDate = (fileDate: string | null | undefined, language: string): string => {
  if (!fileDate) {
    return '—';
  }

  const m = moment(fileDate, dateFormats);
  if (!m.isValid()) {
    return '—';
  }

  return language === 'en' ? m.format('MM/DD/YYYY') : m.format('DD.MM.YYYY');
};

/** Date format used by the backend (e.g. "2017-05-11"). */
export const dateFormat = 'YYYY-MM-DD';

// ---------------------------------------------------------------------------
// Filter functions adapted for PublicationDocument fields
// ---------------------------------------------------------------------------

/**
 * Live text filter — searches across PublicationDocument fields.
 * Mapping from ArchiveList fields:
 *   date → fileDate, header → documentTitle, text → fundName, doctypeheader → documentType
 */
export const filterCol = (doc: any, filterInfo: any): boolean => {
  const filterText = filterInfo.text?.[0] ?? false;
  if (!filterText) {
    return true;
  }

  const filterRegex = new RegExp(escapeStringRegexp(replaceUmlaute(filterText)), 'i');
  let result = false;

  // Map ArchiveList keys to PublicationDocument keys
  const fieldMap: { [key: string]: string } = {
    date: 'fileDate',
    header: 'documentTitle',
    text: 'fundName',
    doctypeheader: 'documentType',
  };

  for (const archiveKey of ['date', 'header', 'text', 'doctypeheader']) {
    if (result) {
      break;
    }

    const docKey = fieldMap[archiveKey];
    const fieldValue = doc[docKey];

    // Some fields (e.g. fundName) are string arrays — test each entry.
    if (Array.isArray(fieldValue)) {
      if (fieldValue.some((value) => value && filterRegex.test(replaceUmlaute(value)))) {
        result = true;
      }
    } else if (filterRegex.test(replaceUmlaute(fieldValue))) {
      result = true;
    }
  }

  // Search the isins array field
  if (!result && Array.isArray(doc.isins)) {
    for (const isinValue of doc.isins) {
      if (!isinValue) {
        continue;
      }
      if (filterRegex.test(replaceUmlaute(isinValue))) {
        result = true;
        break;
      }
    }
  }

  return result;
};

/**
 * Detail filter — matches a document field against selected filter values.
 * Works generically on any field key (matching ArchiveList's implementation).
 */
export const filterColByDetailField = (doc: any, searchFields: any, fieldKey: string): boolean => {
  if (!searchFields || !searchFields[fieldKey] || !searchFields[fieldKey].filterVals.length) {
    return true;
  }

  return replaceUmlaute(searchFields[fieldKey].filterVals).indexOf(replaceUmlaute(doc[fieldKey])) > -1;
};
